/**
 * Nawigacja z wiersza sprawy/decyzji do kartoteki celu (kokpit decyzji + listy spraw + widget
 * pulpitu „oczekujące akceptacje").
 *
 * Dlaczego async i dlaczego tu, a nie w `module-routes.ts`: dokumenty dzielą JEDEN kod celu
 * `crm_core.documents` dla wszystkich 7 klas, więc z samego `(kod, id)` nie da się zbudować trasy
 * synchronicznie — każda klasa ma inną stronę (`/purchase-invoices/:id`, `/contracts/:id`, …).
 * Klasę rozwiązujemy DOPIERO na kliknięciu, po id, przez istniejący batch-endpoint
 * `GET /api/documents?ids=` (ten sam, którym picker/etykiety pól-odwołań rozwiązują dokument po
 * id, gdy klasa jest nieznana). Dzięki temu listy NIE muszą nieść klasy w payloadzie.
 * `module-routes.ts` zostaje czystym, synchronicznym mapowaniem (bez zależności od klienta HTTP).
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DOCUMENT_KARTOTEKI, type DocumentClass, HANDEL_TARGET_MODULE_CODE } from '@saas/shared';

import { useOptionalToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { documentLinkHref, fetchDocumentLink } from '@/lib/handel-magazyn/document-links-api';

import { resolveTargetUrl } from './module-routes';

/**
 * Klasa dokumentu → bazowa ścieżka strony kartoteki. Źródło prawdy: `DOCUMENT_KARTOTEKI`
 * (`@saas/shared`) — ta sama lista, z której powstaje menu/paleta, więc trasy dokumentów nie
 * rozjadą się z resztą aplikacji. Wszystkie 7 klas pokryte; brak wpisu → `undefined` (guard niżej).
 */
const DOCUMENT_CLASS_PATHS: Partial<Record<DocumentClass, string>> = Object.fromEntries(
  DOCUMENT_KARTOTEKI.map((k): [DocumentClass, string] => [k.documentClass, k.path]),
);

interface DocumentsByIdsResponse {
  documents: Array<{ id: string; class: DocumentClass }>;
}

/**
 * Pełny adres kartoteki celu. Dla dokumentu (`crm_core.documents`) dociąga klasę po id przez
 * `GET /api/documents?ids=` i mapuje ją na trasę; dla pozostałych typów = synchroniczny
 * `resolveTargetUrl`. Zwraca `null`, gdy trasy nie da się zbudować (nieznany typ; dokument
 * niewidoczny/miękko skasowany → pusta odpowiedź; klasa bez zmapowanej trasy).
 */
export async function resolveTargetHref(
  moduleCode: string,
  recordId: string,
): Promise<string | null> {
  // Dokument z Symfonii Handel: `recordId` to nasza REZERWACJA identyfikatora, nie dokument.
  // Rodzaj kartoteki i numer w Symfonii poznajemy dopiero z niej — ta sama asynchroniczność
  // co przy dokumentach, z innego powodu. Trasa po drodze sprawdza, czy połączenie wciąż
  // wskazuje tę samą bazę Symfonii; odmowa (409) kończy się brakiem trasy, nie otwarciem
  // CUDZEGO dokumentu o tym samym numerze porządkowym.
  if (moduleCode === HANDEL_TARGET_MODULE_CODE) {
    const link = await fetchDocumentLink(recordId);
    return documentLinkHref({
      kind: link.kind,
      externalId: link.externalId,
      companyId: link.companyId,
    });
  }
  if (moduleCode !== 'crm_core.documents') {
    return resolveTargetUrl(moduleCode, recordId);
  }
  const res = await api.get<DocumentsByIdsResponse>(
    `/documents?ids=${encodeURIComponent(recordId)}`,
  );
  const cls = res.data.documents[0]?.class;
  if (!cls) return null;
  const basePath = DOCUMENT_CLASS_PATHS[cls];
  return basePath ? `${basePath}/${recordId}` : null;
}

/**
 * Hook nawigacji do kartoteki celu — wspólny dla kokpitu, list spraw i widgetu pulpitu (jedno
 * źródło zachowania, zero rozjazdu między ekranami). Dokument rozwiązuje klasę na kliknięciu;
 * błąd sieci/serwera → toast; brak trasy → nic (wiersz i tak jest oznaczony jako nieklikalny
 * przez `isTargetNavigable`). Fire-and-forget: zwraca synchroniczny handler dla `onRowClick`.
 */
export function useNavigateToTarget(): (moduleCode: string, recordId: string) => void {
  const navigate = useNavigate();
  // Null-safe: konsumenci tego hooka (ActiveInstancesTab, MySubmittedInstancesTab…) nie zawsze
  // siedzą pod ToastProviderem w testach. W runtime provider jest zawsze zamontowany globalnie
  // w main.tsx, więc to nie jest ryzyko dla UX-u — wyłącznie footgun konwencji testowej.
  const toast = useOptionalToast();
  const { t } = useTranslation();
  return useCallback(
    (moduleCode: string, recordId: string) => {
      void resolveTargetHref(moduleCode, recordId)
        .then((href) => {
          if (href) {
            navigate(href);
            return;
          }
          // Wiersz-dokument renderujemy zawsze jako klikalny (klasę znamy dopiero po kliknięciu),
          // więc gdy trasy nie da się zbudować — dokument skasowany / brak dostępu / klasa bez
          // strony — dajemy jawny toast zamiast cichego braku reakcji. Nie-dokumentowe cele bez
          // trasy są z góry oznaczone jako nieklikalne (`isTargetNavigable`), więc ich pomijamy.
          if (moduleCode === 'crm_core.documents' || moduleCode === HANDEL_TARGET_MODULE_CODE) {
            toast?.show({
              variant: 'error',
              description: t('tenant.processes.approvals.targetUnavailable'),
            });
          }
        })
        .catch(() => {
          toast?.show({
            variant: 'error',
            description: t('tenant.processes.approvals.openTargetError'),
          });
        });
    },
    [navigate, toast, t],
  );
}
